<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none ordered-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in parts"
        :key="item._id"
        class="ordered-mobile-card"
        :class="{
          'ordered-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <v-btn
          v-if="getMobileOrderPrice(item)"
          class="ordered-mobile-card__price"
          variant="outlined"
          size="x-small"
          @click.stop="openPriceDialog(item)"
        >
          <del
            v-if="getMobileOrderPrice(item).isDiscounted"
            class="text-black overline mr-1"
          >
            {{ getMobileOrderPrice(item).original }}
          </del>
          <span
            :class="
              getMobileOrderPrice(item).isDiscounted
                ? 'text-red font-weight-bold'
                : 'text-black'
            "
          >
            {{ getMobileOrderPrice(item).text }}
          </span>
        </v-btn>

        <div class="ordered-mobile-card__main">
          <div class="ordered-mobile-card__media">
            <button
              type="button"
              class="ordered-mobile-card__image-btn"
              @click.stop="showImages(item.images)"
            >
              <img :src="getPartImageSrc(item)" class="ordered-mobile-card__img" />
            </button>
            <button
              v-if="getPartQrId(item)"
              type="button"
              class="ordered-mobile-card__qr-btn"
              @click.stop="openPartQr(item)"
            >
              {{ getPartQrId(item) }}
            </button>
          </div>

          <div class="ordered-mobile-card__info">
            <div class="ordered-mobile-card__info-top">
               <v-chip
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id?.title
                "
                   class=" text-xs"
                 size="x-small"
              >
                {{ item.institution_id.title }}
              </v-chip>
              <div
                v-if="item.infoCar?.description?.descriptionId?.group"
                class="text-caption font-weight-bold"
              >
                {{
                  item.infoCar.description.descriptionId.group[
                    $store.getters.language
                  ]
                }}
              </div>
              <div
                v-if="item.infoCar?.description?.descriptionId?.category"
                class="text-caption text-grey-darken-1"
              >
                {{
                  item.infoCar.description.descriptionId.category[
                    $store.getters.language
                  ]
                }}
              </div>
              <div
                v-if="item.infoCar?.description?.descriptionId?.title"
                class="font-weight-bold text-primary text-body-2"
              >
                {{
                  item.infoCar.description.descriptionId.title[
                    $store.getters.language
                  ]
                }}
                <span
                  v-if="item.infoCar.description.positionCode"
                  class="text-caption text-grey font-weight-regular"
                >
                  {{ getPosition(item.infoCar.description.positionCode) }}
                </span>
              </div>
            </div>

            <div v-if="item.manufacturer_code" class="text-caption mt-1">
              Cod: <strong>{{ item.manufacturer_code }}</strong>
            </div>
            <div
              v-if="item.order?.id?.date_created"
              class="text-caption text-grey-darken-1 mt-1"
            >
              {{ $t("parts.table.order_date") }}:
              {{ formatDate(item.order.id.date_created) }}
            </div>
            <div v-if="item.shipment_id?.name" class="text-caption mt-1">
              {{ item.shipment_id.name }}
            </div>
            <div
              v-if="item.notes"
              class="text-caption mt-1 pointer text-primary"
              @click.stop="showNotes(item.notes)"
            >
              <span v-if="item.notes.length > 40">
                {{ item.notes.substring(0, 40) }}...
                <v-icon size="14">mdi-eye</v-icon>
                ({{ item.notes.length }})
              </span>
              <span v-else>{{ item.notes }} ({{ item.notes.length }})</span>
            </div>
            <div v-if="item.shelf_data" class="text-caption mt-1">
              {{ item.shelf_data.shelf.room_name }} -
              {{ item.shelf_data.shelf.location_code }}:
              {{ item.shelf_data.shelf.x }}, {{ item.shelf_data.shelf.y }}
              <span class="font-weight-bold ml-1">
                Mag: {{ item.shelf_data.repo }}
              </span>
            </div>
            <div class="text-caption text-grey-darken-1 mt-1">
              {{ formatDate(item.date_created) }}
            </div>
            <div class="mt-1 d-flex align-center flex-wrap ga-3">
              <div class="d-flex align-center ga-1">
               
              <v-rating
                v-if="item.quality"
                :model-value="item.quality.rating"
                color="red"
                readonly
                :size="14"
                density="compact"
              />
               <span class="text-caption text-grey-darken-1 ml-1 mt-2">
                  {{ $t("parts.table.origin") }}:
                </span>
                <v-icon
                  v-if="item.order?.id?.origin === 'whatsapp'"
                  color="green"
                  size="20"
                  class="mt-1"
                >
                  mdi-whatsapp
                </v-icon>
                <img
                  v-else
                  height="20"
                  width="20"
                  src="@/assets/rr-logo.png"
                  class="mt-2"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>

        <div class="ordered-mobile-card__actions">
          <v-chip
            v-if="getOrderStatusMeta(item).label"
            size="small"
            :color="getOrderStatusMeta(item).color"
            variant="tonal"
            class="mr-1 text-caption font-weight-medium"
          >
            {{ getOrderStatusMeta(item).label }}
          </v-chip>

          <v-spacer />

          <v-btn
            v-if="item.order?.id"
            icon
            variant="text"
            size="small"
            @click.stop="showBuyerDetails(buildBuyerDetailsPayload(item))"
          >
            <v-icon size="20" color="grey-darken-2">mdi-account-box</v-icon>
          </v-btn>
          <v-btn
            v-if="item.order?.id"
            icon
            variant="text"
            size="small"
            @click.stop="openActionsDialog(item)"
          >
            <v-icon size="20" color="grey-darken-2">
              mdi-compare-horizontal
            </v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="viewPart(item)">
            <v-icon size="20" color="grey-darken-2">
              mdi-dots-horizontal-circle-outline
            </v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="details(item)">
            <v-icon size="20" color="grey-darken-2">mdi-view-list</v-icon>
          </v-btn>
          <v-btn
            v-if="item.car_id"
            icon
            variant="text"
            size="small"
            @click.stop="viewCar(item.car_id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-car</v-icon>
          </v-btn>
          <v-btn v-else icon variant="text" size="small" disabled>
            <v-icon size="20" color="grey-darken-2">mdi-car-off</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="share(item)">
            <v-icon size="20" color="grey-darken-2">
              mdi-share-variant-outline
            </v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="!loading && parts.length < 1"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="ordered-table-host pb-2">
      <StickyHScroll>
        <v-data-table-server
        v-model:options="pagination"
        :headers="
          $store.getters.role === 'LekoAdmin' ||
          $store.getters.role === 'SuperAdmin'
            ? headersAdmin
            : headers
        "
        :items="parts"
        :page="pagination.page"
        :items-length="totalItems"
        :loading="loading"
        class="elevation-1 nowrap-headers pb-8"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index === activeRow }"
            class="tableRowColor"
            @click="activeRow = activeRow === index ? null : index"
          >
            <!------------------------------------------ID------------------------------------------->
            <!--OLD ID-->
            <td v-if="item.old_id && item.old_id !== undefined">
              <v-row
                class="pointer ma-1 text-center justify-center"
                @click="
                  openQrDialog({
                    id: item.old_id,
                    type: 'inventory',
                    printed: item.printed,
                  })
                "
              >
                <qr-code :size="60" :text="item.old_id" />
                {{
                  item.old_id.slice(item.old_id.length - 6, item.old_id.length)
                }}
              </v-row>
            </td>
            <!--NEW ID-->
            <td v-else class="text-xs">
              <v-row
                class="pointer ma-1 text-center justify-center"
                @click="
                  openQrDialog({
                    id: item._id,
                    type: 'inventory',
                    printed: item.printed,
                  })
                "
              >
                <qr-code :size="60" :text="item._id" />
                {{ item._id.slice(item._id.length - 4, item._id.length) }}
              </v-row>
            </td>
            <!----------------------------------------ACTIONS---------------------------------------->
            <td>
              <v-row no-gutters class="d-flex flex-nowrap">
                <p style="display: none">
                  {{ item._id }}
                </p>
                <v-btn
                  v-if="item.order.id"
                  icon
                  variant="text"
                  @click="showBuyerDetails(buildBuyerDetailsPayload(item))"
                >
                  <v-icon color="grey-darken-2"> mdi-account-box </v-icon>
                </v-btn>
                <!-- :disabled="
                  item.order.id.status !== 0 &&
                  item.order.id.status !== 5 &&
                  item.order.id.status !== 7
                " -->
                <v-btn
                  v-if="item.order.id"
                  icon
                  variant="text"
                  @click="openActionsDialog(item)"
                >
                  <v-icon color="grey-darken-2">
                    mdi-compare-horizontal
                  </v-icon>
                </v-btn>
                <v-btn icon variant="text" @click="viewPart(item)">
                  <v-icon color="grey-darken-2">
                    mdi-dots-horizontal-circle-outline
                  </v-icon>
                </v-btn>
              </v-row>
              <v-row no-gutters class="d-flex flex-nowrap">
                <v-btn icon variant="text" @click="details(item)">
                  <v-icon color="grey-darken-2"> mdi-view-list </v-icon>
                </v-btn>
                <v-btn
                  v-if="item.car_id"
                  icon
                  variant="text"
                  @click="viewCar(item.car_id)"
                >
                  <v-icon color="grey-darken-2"> mdi-car </v-icon>
                </v-btn>
                <v-btn v-else icon variant="text" disabled>
                  <v-icon color="grey-darken-2"> mdi-car-off </v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="share(item)">
                  <v-icon color="grey-darken-2">
                    mdi-share-variant-outline
                  </v-icon>
                </v-btn>
              </v-row>
            </td>

            <!--------------------------------------ORDER DATE--------------------------------------->
            <td>
              <span
                v-if="item.order.id && item.order.id.date_created"
                style="white-space: pre"
              >
                {{ formatDate(item.order.id.date_created) }}
              </span>
              <span v-else>/</span>
            </td>
            <!--------------------------------------LEKO GROUP--------------------------------------->
            <td>
              <v-row class="d-block">
                <v-col>
                  <span
                    v-if="
                      item.infoCar &&
                      item.infoCar.description &&
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.group
                    "
                    class="text-xs font-weight-bold"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    {{
                      item.infoCar.description.descriptionId.group[
                        $store.getters.language
                      ]
                    }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------------GROUP----------------------------------------->
            <td>
              <v-row class="d-block">
                <v-col>
                  <span
                    v-if="
                      item.infoCar &&
                      item.infoCar.description &&
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.category
                    "
                    class="text-xs font-weight-bold"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    {{
                      item.infoCar.description.descriptionId.category[
                        $store.getters.language
                      ]
                    }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------------TITLE----------------------------------------->
            <td>
              <v-row class="d-block justify-center" style="width: 180px">
                <v-col v-if="item.infoCar && item.infoCar.description">
                  <span
                    v-if="
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.title
                    "
                    class="text-xs font-weight-bold"
                  >
                    {{
                      item.infoCar.description.descriptionId.title[
                        $store.getters.language
                      ]
                    }}
                  </span>
                  <span
                    v-if="item.infoCar.description.positionCode"
                    class="text-xs"
                  >
                    {{ getPosition(item.infoCar.description.positionCode) }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------MANUFACTURER CODE----------------------------------->
            <td class="text-xs">
              {{ item.manufacturer_code }}
            </td>
            <!--------------------------------------INSTITUTION-------------------------------------->
            <td
              v-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin') &&
                item.institution_id
              "
              class="text-xs"
            >
              <v-chip>
                {{ item.institution_id.title }}
              </v-chip>
            </td>
            <td
              v-else-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin') &&
                !item.institution_id
              "
            />
            <!----------------------------------------IMAGES----------------------------------------->
            <td>
              <v-row class="justify-center">
                <p style="display: none">
                  {{ item.old_id }}
                </p>
                <img
                  :src="
                    item.images &&
                    item.images.length > 0 &&
                    item.images[0].thumb
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="showImages(item.images)"
                />
              </v-row>
            </td>
            <!----------------------------------------ORIGIN----------------------------------------->
            <td>
              <v-row class="justify-center">
                <v-icon
                  v-if="
                    item.order &&
                    item.order.id &&
                    item.order.id.origin &&
                    item.order.id.origin === 'whatsapp'
                  "
                  color="green"
                >
                  mdi-whatsapp
                </v-icon>
                <!-- <img
                v-else
                height="32"
                width="32"
                src="@/assets/icons/online-shopping.png"
              > -->
                <img v-else height="30" width="30" src="@/assets/rr-logo.png" />
              </v-row>
            </td>
            <!-----------------------------------------PRICE----------------------------------------->
            <td class="text-center">
              <template
                v-if="item.order && item.order.id && item.order.id.price"
              >
                <v-btn variant="outlined" @click="openPriceDialog(item)">
                  <del
                    v-if="
                      calculateDiscountedPrice(
                        item.order.id.price.logistic_price,
                        item,
                      ).isDiscounted
                    "
                    class="mr-2 grey--text"
                  >
                    {{ item.order.id.price.logistic_price }} {{ item.currency }}
                  </del>

                  <span
                    :class="[
                      'font-weight-bold',
                      calculateDiscountedPrice(
                        item.order.id.price.logistic_price,
                        item,
                      ).isDiscounted
                        ? 'text-red'
                        : 'text-black',
                    ]"
                  >
                    {{
                      calculateDiscountedPrice(
                        item.order.id.price.logistic_price,
                        item,
                      ).finalPrice
                    }}
                    {{ item.currency }}
                  </span>
                </v-btn>

                <!-- <div
                v-if="
                  calculateDiscountedPrice(
                    item.order.id.price.logistic_price,
                    item,
                  ).isDiscounted
                "
                class="caption mt-1 text-red font-weight-bold"
              >
                ( -
                {{
                  calculateDiscountedPrice(
                    item.order.id.price.logistic_price,
                    item,
                  ).discount
                }}% OFF )
              </div> -->
              </template>

              <span v-else class="'text-grey"> N/A </span>
            </td>
            <!-----------------------------------------NOTES----------------------------------------->
            <td class="text-xs">
              <v-row
                align="center"
                justify="center"
                class="pointer"
                style="min-width: 125px !important"
                @click="showNotes(item.notes)"
              >
                <span v-if="item.notes && item.notes.length > 40">
                  {{ item.notes.substring(0, 40) }}... <v-icon>mdi-eye</v-icon
                  >{{ item.notes ? "(" + item.notes.length + ")" : "" }}
                </span>
                <span v-else
                  >{{ item.notes
                  }}{{ item.notes ? "(" + item.notes.length + ")" : "" }}</span
                >
              </v-row>
            </td>
            <!---------------------------------------LOCATION---------------------------------------->
            <td v-if="item.shelf_data" class="text-xs">
              <span>
                {{ item.shelf_data.shelf.room_name }} -
                {{ item.shelf_data.shelf.location_code }}:
                {{ item.shelf_data.shelf.x }},
                {{ item.shelf_data.shelf.y }}
              </span>

              <div class="text-no-wrap">
                <span class="caption">Mag: </span>
                <span class="font-weight-bold caption">{{
                  item.shelf_data.repo
                }}</span>
              </div>
            </td>
            <td v-else class="text-xs">-</td>
            <!---------------------------------------SHIPMENT---------------------------------------->
            <td>
              <div
                v-if="item.shipment_id && item.shipment_id.name"
                style="width: 180px"
                class="pl-1"
              >
                {{ item.shipment_id.name }}
              </div>
            </td>
            <!-------------------------------------DATE CREATED-------------------------------------->

            <!----------------------------------------STATUS----------------------------------------->
            <td class="pt-2 pb-1 text-center justify-center">
              <div>
                <v-rating
                  v-if="item.quality"
                  :model-value="item.quality.rating"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
                <v-rating
                  v-else
                  :model-value="0"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
              </div>
              <div class="text-center text-truncate pb-2">
                {{ formatDate(item.date_created) }}
              </div>
              <div
                v-if="item.order && item.order.id && item.order.id.status === 0"
              >
                <v-chip label variant="outlined" color="amber">
                  {{ $t("parts.status.waiting_confirmation") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 1
                "
              >
                <v-chip label variant="outlined" color="success">
                  {{ $t("parts.status.confirmed") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 2
                "
              >
                <v-chip label variant="outlined" color="success">
                  {{ $t("parts.status.verified") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 3
                "
              >
                <v-chip label variant="outlined" color="primary">
                  {{ $t("parts.status.shipped") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 4
                "
              >
                <v-chip label variant="outlined" color="error">
                  {{ $t("parts.status.blocked") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 5
                "
              >
                <v-chip label variant="outlined" color="amber">
                  {{ $t("parts.status.waiting_return_confirmation") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 6
                "
              >
                <v-chip label variant="outlined" color="amber">
                  {{ $t("parts.status.return_confirmed") }}
                </v-chip>
              </div>
              <div
                v-else-if="
                  item.order && item.order.id && item.order.id.status === 7
                "
              >
                <v-chip label variant="outlined" color="amber">
                  {{ $t("parts.status.returned") }}
                </v-chip>
              </div>
            </td>
            <!--------------------------------------------------------------------------------------->
          </tr>
          <tr v-if="parts && parts.length < 1">
            <td :colspan="headers.length" class="text-center pa-4">
              <v-alert type="info" border="left" color="red lighten-2" text>
                No data available.
              </v-alert>
            </td>
          </tr>
        </template>
      </v-data-table-server>
      </StickyHScroll>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  props: ["search"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    loading: false,
    activeRow: null,
    parts: [],
    totalItems: 0,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["order.id.date_created"],
      sortBy: [{ key: "order.id.date_created", order: "desc" }],
      sortDesc: [true],
    },
    headers: [
      {
        title: "",
        sortable: true,
        key: "_id",
      },
      {
        title: "",
        sortable: false,
        key: "actions",
      },
      {
        title: "",
        sortable: true,
        key: "order.id.date_created",
      },
      {
        title: "",
        sortable: true,
        key: "leko_group",
      },
      {
        title: "",
        sortable: true,
        key: "group",
      },
      {
        title: "",
        sortable: true,
        key: "infoCar.description.descriptionId.title.it",
      },
      {
        title: "",
        sortable: false,
        key: "manufacturer_code",
      },
      {
        title: "",
        sortable: false,
        key: "",
      },
      {
        title: "",
        sortable: true,
        key: "origin",
      },
      {
        title: "",
        sortable: true,
        key: "price",
      },
      {
        title: "",
        sortable: false,
        key: "notes",
      },
      {
        title: "",
        sortable: true,
        key: "shelf_data.shelf.room_name",
      },
      {
        title: "",
        sortable: true,
        key: "shipment_id",
      },
      // {
      //   title: '',
      //   sortable: true,
      //   key: 'quality',
      // },
      {
        title: "",
        sortable: true,
        key: "",
      },
      // {
      //   title: '',
      //   sortable: true,
      //   key: 'status',
      // },
    ],
    headersAdmin: [
      {
        title: "",
        sortable: true,
        key: "_id",
      },
      {
        title: "",
        sortable: false,
        key: "actions",
      },
      {
        title: "",
        sortable: true,
        key: "order.id.date_created",
      },
      {
        title: "",
        sortable: true,
        key: "leko_group",
      },
      {
        title: "",
        sortable: true,
        key: "group",
      },
      {
        title: "",
        sortable: true,
        key: "infoCar.description.descriptionId.title.it",
      },
      {
        title: "",
        sortable: false,
        key: "manufacturer_code",
      },
      {
        title: "",
        sortable: false,
        key: "institution_id",
      },
      {
        title: "",
        sortable: false,
        key: "",
      },
      {
        title: "",
        sortable: true,
        key: "origin",
      },
      {
        title: "",
        sortable: true,
        key: "price",
      },
      {
        title: "",
        sortable: false,
        key: "notes",
      },
      {
        title: "",
        sortable: true,
        key: "shelf_data.shelf.room_name",
      },
      {
        title: "",
        sortable: true,
        key: "shipment_id",
      },
      // {
      //   title: '',
      //   sortable: true,
      //   key: 'quality',
      // },
      {
        title: "",
        sortable: true,
        key: "",
      },
      // {
      //   title: '',
      //   sortable: true,
      //   key: 'status',
      // },
    ],
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    searchObject() {
      return this.$props.search;
    },
  },
  watch: {
    pagination: {
      handler() {
        if (this.searchObject.type === null) {
          this.getParts();
        } else if (this.searchObject.type === "regularSearch") {
          this.regularSearch(this.searchObject.data);
        } else if (this.searchObject.type === "advancedSearch") {
          this.advancedSearch(this.searchObject.data);
        } else {
          //
        }
      },
      deep: true,
    },
    searchObject: {
      handler(value) {
        if (value.type === null) {
          this.getParts();
        } else if (value.type === "regularSearch") {
          this.regularSearch(value.data);
        } else if (value.type === "advancedSearch") {
          this.advancedSearch(value.data);
        } else {
          //
        }
      },
    },
    parts(value) {
      this.$emit("items", value);
    },
  },
  mounted() {
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("parts.table.qr_code");
      this.headers[1].title = this.$t("parts.table.actions");
      this.headers[2].title = this.$t("parts.table.order_date");
      this.headers[3].title = this.$t("parts.table.leko_group");
      this.headers[4].title = this.$t("parts.table.group");
      this.headers[5].title = this.$t("parts.table.title");
      this.headers[6].title = this.$t("parts.table.manufacturer_code");
      this.headers[7].title = this.$t("parts.table.images");
      this.headers[8].title = this.$t("parts.table.origin");
      this.headers[9].title = this.$t("parts.table.price");
      this.headers[10].title = this.$t("parts.table.notes");
      this.headers[11].title = this.$t("parts.table.location");
      this.headers[12].title = this.$t("parts.table.shipment");
      // this.headers[13].title = this.$t('parts.table.quality');
      this.headers[13].title = this.$t("parts.table.date_created");
      // this.headers[14].title = this.$t('parts.table.status');
      this.headersAdmin[0].title = this.$t("parts.table.qr_code");
      this.headersAdmin[1].title = this.$t("parts.table.actions");
      this.headersAdmin[2].title = this.$t("parts.table.order_date");
      this.headersAdmin[3].title = this.$t("parts.table.leko_group");
      this.headersAdmin[4].title = this.$t("parts.table.group");
      this.headersAdmin[5].title = this.$t("parts.table.title");
      this.headersAdmin[6].title = this.$t("parts.table.manufacturer_code");
      this.headersAdmin[7].title = this.$t("parts.table.institution");
      this.headersAdmin[8].title = this.$t("parts.table.images");
      this.headersAdmin[9].title = this.$t("parts.table.origin");
      this.headersAdmin[10].title = this.$t("parts.table.price");
      this.headersAdmin[11].title = this.$t("parts.table.notes");
      this.headersAdmin[12].title = this.$t("parts.table.location");
      this.headersAdmin[13].title = this.$t("parts.table.shipment");
      // this.headersAdmin[14].title = this.$t('parts.table.quality');
      this.headersAdmin[14].title = this.$t("parts.table.date_created");
      // this.headersAdmin[15].title = this.$t('parts.table.status');
    },
    getSortParam() {
      return (
        this.pagination.sortBy?.[0] || {
          key: "order.id.date_created",
          order: "desc",
        }
      );
    },
    buildListQueryParams(page, limit) {
      const sort = this.getSortParam();
      return `institution_id=${this.$store.getters.institutionId}&role=${
        this.$store.getters.role
      }&limit=${limit}&page=${page}&sort_by=${sort.key}&descending=${
        sort.order === "desc"
      }`;
    },
    async fetchAllMatchingParts() {
      const total = this.totalItems || 0;
      if (!total) return [...(this.parts || [])];
      const limit = Math.min(total, 10000);
      const base = this.buildListQueryParams(1, limit);
      const search = this.searchObject;
      let path;
      let query;
      if (!search || search.type === null) {
        path = "/part/ordered/datatable";
        query = base;
      } else if (search.type === "regularSearch") {
        const { data } = search;
        path = "/part/ordered/search/regular";
        query = `${base}&search_field=${data.searchField || ""}&title_code=${
          data.titleCode || ""
        }&title_codes=${data.titleCodes || ""}&type=${
          data.type || ""
        }&language=${this.$store.getters.language}`;
      } else if (search.type === "advancedSearch") {
        const { data } = search;
        const fields = data.advancedSearchFields || {};
        path = "/part/ordered/search/advanced";
        query = `${base}&search_field=${data.searchField || ""}&language=${
          this.$store.getters.language
        }&title_code=${data.titleCode || ""}&title_codes=${
          data.titleCodes || ""
        }&type=${data.type || ""}&brand=${fields.brand || ""}&model=${
          fields.model || ""
        }&version=${fields.version || ""}&institution=${
          fields.institution || ""
        }&shipment=${fields.shipment || ""}&status=${
          fields.status || ""
        }&quality=${fields.quality || ""}&price=${fields.price || ""}&start_date=${
          fields.startDate || ""
        }&end_date=${fields.endDate || ""}&origin=${fields.origin || ""}`;
      } else {
        return [...(this.parts || [])];
      }
      const response = await getRequest(path, query);
      return response.parts || [];
    },
    getParts() {
      this.loading = true;
      const sort = this.getSortParam();
      getRequest(
        "/part/ordered/datatable",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.$scrollToTop();
          this.parts = response.parts;
          this.totalItems = response.count;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    regularSearch(data) {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "order.id.date_created",
        order: "desc",
      };
      getRequest(
        "/part/ordered/search/regular",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&title_code=${
          data.titleCode
        }&title_codes=${data.titleCodes}&type=${
          data.type
        }&language=${this.$store.getters.language}`,
      )
        .then((response) => {
          this.$scrollToTop();
          this.totalItems = response.count;
          this.parts = response.parts;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    advancedSearch(data) {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "order.id.date_created",
        order: "desc",
      };
      getRequest(
        "/part/ordered/search/advanced",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&language=${
          this.$store.getters.language
        }&title_code=${data.titleCode}&title_codes=${data.titleCodes}&type=${
          data.type
        }&brand=${data.advancedSearchFields.brand}&model=${
          data.advancedSearchFields.model
        }&version=${data.advancedSearchFields.version}&institution=${
          data.advancedSearchFields.institution
        }&shipment=${data.advancedSearchFields.shipment}&status=${
          data.advancedSearchFields.status
        }&quality=${data.advancedSearchFields.quality}&price=${
          data.advancedSearchFields.price
        }&start_date=${
          data.advancedSearchFields.startDate
        }&end_date=${data.advancedSearchFields.endDate}&origin=${
          data.advancedSearchFields.origin || ""
        }`,
      )
        .then((response) => {
          this.$scrollToTop();
          this.totalItems = response.count;
          this.parts = response.parts;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    details(data) {
      this.$emit("details", data);
    },
    viewCar(carId) {
      this.$emit("view-car", { carId });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    openPriceDialog(data) {
      if (data.institution_id.rrMember === true) {
        this.$emit("rr-price", data);
      } else {
        this.$emit("price", data.order.id.price);
      }
    },
    buildBuyerDetailsPayload(item) {
      const order = item?.order?.id || {};
      const lang = this.$store.getters.language || "it";
      const partTitle =
        item?.infoCar?.description?.descriptionId?.title?.[lang] ||
        item?.infoCar?.description?.descriptionId?.title?.it ||
        "";
      const orderDate = order.date_created
        ? this.$t("buyer_details.order_of", {
            date: this.formatShortDate(order.date_created),
          })
        : "";
      return {
        id: order._id,
        checkout_information: order.checkout_information,
        buyerDetails: order.buyer_details,
        drop_shipping: order.drop_shipping,
        meta: [orderDate, partTitle, item?.manufacturer_code].filter(Boolean),
      };
    },
    formatShortDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("D MMM YYYY");
    },
    showBuyerDetails(data) {
      this.$emit("buyer-details", data);
    },
    showImages(images) {
      this.$emit("images", { type: "string", images });
    },
    openActionsDialog(data) {
      this.$emit("actions", data);
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["order.id.date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "order.id.date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
    getPosition(code) {
      switch (code) {
        case "S001":
          return this.$t("left");
        case "S002":
          return this.$t("right");
        default:
          return "";
      }
    },
    showNotes(notes) {
      this.$emit("show-notes", notes);
    },

    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    viewPart(data) {
      this.$emit("view-part", data);
    },
    share(data) {
      this.$emit("share", data);
    },
    getPartQrId(item) {
      if (item.old_id) {
        return item.old_id.slice(item.old_id.length - 6, item.old_id.length);
      }
      if (item._id) {
        return item._id.slice(item._id.length - 4, item._id.length);
      }
      return "";
    },
    openPartQr(item) {
      if (item.old_id) {
        this.openQrDialog({
          id: item.old_id,
          type: "inventory",
          printed: item.printed,
        });
      } else {
        this.openQrDialog({
          id: item._id,
          type: "inventory",
          printed: item.printed,
        });
      }
    },
    getPartImageSrc(item) {
      if (item.images?.length > 0 && item.images[0].thumb) {
        return `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`;
      }
      return "https://portal.lekotech.com/logo_small.png";
    },
    getMobileOrderPrice(item) {
      if (!item.order?.id?.price?.logistic_price) return null;
      const discount = this.calculateDiscountedPrice(
        item.order.id.price.logistic_price,
        item,
      );
      return {
        text: `${discount.finalPrice} ${item.currency || ""}`.trim(),
        original: `${item.order.id.price.logistic_price} ${item.currency || ""}`.trim(),
        isDiscounted: discount.isDiscounted,
      };
    },
    getOrderStatusMeta(item) {
      const status = item.order?.id?.status;
      if (status === 0) {
        return {
          label: this.$t("parts.status.waiting_confirmation"),
          color: "amber",
        };
      }
      if (status === 1) {
        return {
          label: this.$t("parts.status.confirmed"),
          color: "success",
        };
      }
      if (status === 2) {
        return {
          label: this.$t("parts.status.verified"),
          color: "success",
        };
      }
      if (status === 3) {
        return {
          label: this.$t("parts.status.shipped"),
          color: "primary",
        };
      }
      if (status === 4) {
        return {
          label: this.$t("parts.status.blocked"),
          color: "error",
        };
      }
      if (status === 5) {
        return {
          label: this.$t("parts.status.waiting_return_confirmation"),
          color: "amber",
        };
      }
      if (status === 6) {
        return {
          label: this.$t("parts.status.return_confirmed"),
          color: "amber",
        };
      }
      if (status === 7) {
        return {
          label: this.$t("parts.status.returned"),
          color: "amber",
        };
      }
      return { label: "", color: "grey" };
    },
    calculateDiscountedPrice(originalPrice, item) {
      if (
        !item.order ||
        !item.order.id ||
        !item.order.id.origin ||
        !item.institution_id
      ) {
        return { finalPrice: originalPrice, discount: 0, isDiscounted: false };
      }

      const price = parseFloat(originalPrice);

      // Same conditions you used before
      const isEligibleOrigin =
        item.order && item.order.id && item.order.id.origin !== "whatsapp";

      const isEligibleInstitution =
        item.institution_id && item.institution_id.rrMember === true;

      // If not eligible → no discount
      if (!isEligibleOrigin || !isEligibleInstitution) {
        return {
          finalPrice: price.toFixed(2),
          discount: 0,
          isDiscounted: false,
        };
      }
      // -------------------------
      // NEW %RR RULES
      // -------------------------
      let rrPercent = 0;

      if (price >= 0 && price <= 299) {
        rrPercent = 20;
      } else if (price >= 300 && price <= 999) {
        rrPercent = 15;
      } else if (price >= 1000 && price <= 1999) {
        rrPercent = 10;
      } else if (price >= 2000) {
        rrPercent = 5;
      }

      // Convert % to decimals
      const rrDiscount = rrPercent / 100;

      // -------------------------
      // NEW PRICE FORMULA
      // Prezzo tot - %RR + 7% + spedizione
      // -------------------------

      const shipping =
        item.shippingCosts && item.shippingCosts.rr
          ? parseFloat(item.shippingCosts.rr)
          : 0;

      const finalPrice = price - price * rrDiscount + price * 0.07 + shipping;

      return {
        finalPrice: finalPrice.toFixed(2),
        discount: rrPercent, // same variable name
        isDiscounted: rrPercent > 0,
      };
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .ordered-table-host :deep(.v-table__wrapper),
  .ordered-table-host :deep(.v-data-table__wrapper),
  .ordered-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .ordered-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .ordered-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .ordered-mobile-list {
    display: none !important;
  }
}

.ordered-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.ordered-mobile-card__price {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.7rem !important;
  max-width: 55%;
  height: auto !important;
  min-height: 0 !important;
}
.ordered-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.ordered-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.ordered-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.ordered-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.ordered-mobile-card__img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}
.ordered-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.ordered-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.ordered-mobile-card__info-top {
  padding-right: 88px;
}
.ordered-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 2px 4px;
}
</style>
